import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Callout,
  IconArrowRight,
  JourneyTrack,
  Surface,
} from '../../ui/v2';
import {
  type Checklist,
  nextStep,
  phaseViews,
  recommendedItems,
  showsGettingStarted,
  sinceLiveLine,
  stepsLeft,
  stepsSinceLive,
  summaryLine,
} from './onboardingChecklist';
import { useOnboardingChecklist } from './useOnboardingChecklist';

/**
 * Top of Dashboard home until the store is live: where setup stands, the
 * next step, and a way back to Getting started. Once live it only appears
 * for steps added since (see `AddedSinceLive`). Renders nothing while the
 * checklist is turned off.
 */
export default function SetupProgressCard() {
  const navigate = useNavigate();
  const { data } = useOnboardingChecklist({ fresh: true });
  // A store with the checklist turned off gets only `{ enabled: false }`, so
  // nothing may read past `enabled` (phaseViews reads `items`).
  const phases = useMemo(() => (data?.enabled ? phaseViews(data) : []), [data]);
  if (!data || !showsGettingStarted(data)) return null;
  if (data.status === 'live') return <AddedSinceLive checklist={data} />;

  const ready = data.status === 'ready';
  const left = stepsLeft(data);
  const open = recommendedItems(data);

  return (
    <div className="v2-rise-in flex flex-col gap-2.5">
      <Surface
        as="section"
        aria-label="Setup"
        padded
        className="border-v2-accent/25 bg-v2-accent/[0.04]"
      >
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4">
          <div className="min-w-0 flex-1">
            <span className="block text-v2-10 font-semibold uppercase tracking-[0.1em] text-v2-accent">
              Getting started
            </span>
            <h2 className="mt-1 text-v2-18 font-bold tracking-[-0.015em] text-v2-fg">
              {ready
                ? 'Everything’s set. Forward your number to go live.'
                : `Your agent isn’t live yet · ${left} step${left === 1 ? '' : 's'} left`}
            </h2>
            <p className="mt-1 text-v2-13 text-v2-fg-3">
              {ready
                ? 'Guests calling your main number still reach your staff until you forward it.'
                : data.next_step
                  ? `Next up: ${data.next_step.title} · ${summaryLine(data.counts)}`
                  : summaryLine(data.counts)}
            </p>
          </div>
          <Button
            variant="primary"
            trailing={<IconArrowRight size={14} />}
            className="h-10 px-4 text-v2-14"
            onClick={() =>
              navigate(
                ready ? '/getting-started?step=go_live' : '/getting-started',
              )
            }
          >
            {ready ? 'Go live' : 'Continue setup'}
          </Button>
        </div>
        {phases.length > 0 && (
          <JourneyTrack
            phases={phases}
            className="mt-5 border-t border-v2-line/80 pt-4"
          />
        )}
      </Surface>
      {open.map((item) => (
        <Callout key={item.key} tone="warning" title={item.title}>
          {item.detail}
        </Callout>
      ))}
    </div>
  );
}

/**
 * A live store took on something new, like phone orders: a short note
 * pointing to the step, without the setup progress it's long past.
 */
function AddedSinceLive({ checklist }: { checklist: Checklist }) {
  const navigate = useNavigate();
  const left = stepsLeft(checklist);
  const next = nextStep(checklist);
  const waiting = stepsSinceLive(checklist).find(
    (item) => item.status === 'waiting',
  );
  return (
    <Surface
      as="section"
      aria-label="Setup"
      padded
      className="v2-rise-in border-v2-accent/25 bg-v2-accent/[0.04]"
    >
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4">
        <div className="min-w-0 flex-1">
          <span className="block text-v2-10 font-semibold uppercase tracking-[0.1em] text-v2-accent">
            Getting started
          </span>
          <h2 className="mt-1 text-v2-18 font-bold tracking-[-0.015em] text-v2-fg">
            {next ? next.title : (waiting?.title ?? sinceLiveLine(left))}
          </h2>
          <p className="mt-1 text-v2-13 text-v2-fg-3">
            {next
              ? `Your agent is live. ${sinceLiveLine(left)}.`
              : 'We’re on it. Nothing to do on your side for now.'}
          </p>
        </div>
        {next && (
          <Button
            variant="primary"
            trailing={<IconArrowRight size={14} />}
            className="h-10 px-4 text-v2-14"
            onClick={() => navigate(`/getting-started?step=${next.key}`)}
          >
            Finish setup
          </Button>
        )}
      </div>
    </Surface>
  );
}
